/**
 * TrialFollowupConversionWidget — Iter 310
 *
 * Marketing-Dashboard Widget: zeigt wie effektiv die Trial-Follow-Up
 * Voucher-Kampagne ist. Konversion = wie viele Probetraining-Nichtbucher
 * konnten zurückgewonnen werden.
 *
 * Endpoint: GET /api/admin/trial-followup/stats?days=30
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Users, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [7, 30, 90];

const TrialFollowupConversionWidget = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/admin/trial-followup/stats?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(res.data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading && !stats) {
    return null;
  }

  return (
    <Card data-testid="trial-followup-conversion-widget">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Gift className="w-5 h-5 text-purple-600" />
              Probetraining Follow-Up · Conversion
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Wie viele Probetraining-Nichtbucher konnte das Follow-Up
              zurückgewinnen?
            </CardDescription>
          </div>
          <div className="flex gap-1" data-testid="trial-stats-window">
            {WINDOWS.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? 'default' : 'outline'}
                onClick={() => setDays(d)}
                data-testid={`trial-stats-window-${d}`}
              >
                {d}T
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!stats || stats.sent === 0 ? (
          <div
            className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500"
            data-testid="trial-stats-empty"
          >
            In den letzten {days} Tagen wurden noch keine Trial-Follow-Up
            Voucher ausgestellt. Sobald Kunden ihr Probetraining hatten
            ohne Folgebuchung, springt der Cron-Job ein.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                icon={<Users className="w-4 h-4 text-blue-600" />}
                label="Voucher gesendet"
                value={stats.sent}
                color="text-blue-700"
                testId="trial-sent"
              />
              <StatCard
                icon={<Gift className="w-4 h-4 text-emerald-600" />}
                label="Eingelöst (Folgebuchung)"
                value={stats.used}
                color="text-emerald-700"
                testId="trial-used"
              />
              <StatCard
                icon={<TrendingUp className="w-4 h-4 text-purple-600" />}
                label="Conversion"
                value={stats.conversion_rate !== null ? `${stats.conversion_rate}%` : '—'}
                color="text-purple-700"
                testId="trial-conversion"
              />
            </div>

            {stats.by_excluded_trainer?.length > 0 && (
              <div className="border-t pt-3" data-testid="trial-trainer-table">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-700 mb-2">
                  Performance je Original-Trainer
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Original-Trainer = der Trainer dessen Probetraining nicht zur Folgebuchung führte.
                  Der Kunde wird automatisch zu einem ANDEREN Trainer geleitet.
                </p>
                <div className="space-y-1.5">
                  {stats.by_excluded_trainer.map((t) => (
                    <div
                      key={t.trainer_id || 'unknown'}
                      data-testid={`trial-trainer-row-${t.trainer_id || 'unknown'}`}
                      className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm"
                    >
                      <span className="font-medium text-gray-800">
                        {t.trainer_name}
                      </span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-600">
                          {t.used}/{t.sent} eingelöst
                        </span>
                        <span className={`font-bold px-2 py-0.5 rounded-full ${
                          t.winback_rate >= 50
                            ? 'bg-emerald-100 text-emerald-700'
                            : t.winback_rate >= 25
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {t.winback_rate}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const StatCard = ({ icon, label, value, color, testId }) => (
  <div className="rounded-lg bg-gray-50 p-3" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-xs text-gray-600">
      {icon}
      {label}
    </div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </div>
);

export default TrialFollowupConversionWidget;
